import { useCallback, useEffect, useMemo, useState } from "react";
import type { DataSource } from "../api/dataSource";
import { SymbolLabel } from "../components/SymbolLabel";
import { AccountBadge, accountDisplayFor } from "../lib/accountDisplay";
import type { Account, ClosedStockTrade } from "../types";

type Props = {
  accounts: Account[];
  listClosedStockTrades: DataSource["listClosedStockTrades"];
};

const money = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

const numberFmt = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 6,
});

const dtFmt = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});

function fmtSignedMoney(value: number): string {
  if (!Number.isFinite(value)) return "$0.00";
  const abs = money.format(Math.abs(value));
  return value >= 0 ? `+${abs}` : `-${abs}`;
}

function fmtPx(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "N/A";
  return money.format(value);
}

function fmtQty(value: number): string {
  if (!Number.isFinite(value)) return "0";
  return numberFmt.format(value);
}

function fmtDateTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return dtFmt.format(d);
}

function fmtR(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "N/A";
  return `${value.toFixed(2)}R`;
}

function exitLabel(value: ClosedStockTrade["exitLegType"]): string {
  if (value === "exit") return "manual";
  if (value === "adjustment") return "trim";
  if (value === "target_fallback") return "target fallback";
  if (value === "stop_fallback") return "stop fallback";
  return value;
}

export const ClosedTradesScreen = ({
  accounts,
  listClosedStockTrades,
}: Props) => {
  const stockAccounts = useMemo(
    () =>
      accounts.filter(
        (account) => (account.assetClass ?? "equity") === "equity",
      ),
    [accounts],
  );
  const [brokerAccountId, setBrokerAccountId] = useState("");
  const [symbol, setSymbol] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [asOf, setAsOf] = useState<string | null>(null);
  const [rows, setRows] = useState<ClosedStockTrade[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listClosedStockTrades({
        brokerAccountId: brokerAccountId || undefined,
        symbol: symbol.trim() || undefined,
        from: from || undefined,
        to: to || undefined,
        limit: 100,
      });
      setAsOf(result.asOf);
      setRows(result.rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setRows([]);
      setAsOf(null);
    } finally {
      setLoading(false);
    }
  }, [brokerAccountId, from, listClosedStockTrades, symbol, to]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalPnl = rows.reduce((sum, row) => sum + row.realizedPnl, 0);

  return (
    <div data-screen-label="Trade history">
      <div className="tp-page-head">
        <h1>Trade history</h1>
        <div className="meta">
          {rows.length} closed stock exit{rows.length === 1 ? "" : "s"} ·{" "}
          {fmtSignedMoney(totalPnl)}
        </div>
      </div>

      <form
        className="tp-actionbar"
        onSubmit={(event) => {
          event.preventDefault();
          void load();
        }}
      >
        <select
          className="tp-input"
          aria-label="Account"
          value={brokerAccountId}
          onChange={(event) => setBrokerAccountId(event.target.value)}
        >
          <option value="">All stock accounts</option>
          {stockAccounts.map((account) => (
            <option key={account.brokerAccountId} value={account.brokerAccountId}>
              {account.label}
            </option>
          ))}
        </select>
        <input
          className="tp-input"
          aria-label="Symbol"
          placeholder="Symbol"
          value={symbol}
          onChange={(event) => setSymbol(event.target.value.toUpperCase())}
          style={{ width: 120 }}
        />
        <input
          className="tp-input"
          aria-label="From date"
          type="date"
          value={from}
          onChange={(event) => setFrom(event.target.value)}
        />
        <input
          className="tp-input"
          aria-label="To date"
          type="date"
          value={to}
          onChange={(event) => setTo(event.target.value)}
        />
        <button type="submit" className="tp-chip-btn" disabled={loading}>
          {loading ? "Loading" : "Refresh"}
        </button>
      </form>

      <div className="tp-tile">
        <div className="tp-tile-head">
          <span className="label">Closed stock trades</span>
          <span className="tp-muted">
            {asOf ? `as of ${fmtDateTime(asOf)}` : "source: local fills"}
          </span>
        </div>
        <div className="tp-tile-body" style={{ padding: 0 }}>
          {error ? (
            <div className="tp-empty-state">{error}</div>
          ) : (
            <div className="tp-table-scroll">
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>Closed</th>
                    <th>Symbol</th>
                    <th>Side</th>
                    <th className="right">Qty</th>
                    <th className="right">Entry</th>
                    <th className="right">Exit</th>
                    <th className="right">P&amp;L</th>
                    <th className="right">R</th>
                    <th>Exit type</th>
                    <th>Strategy</th>
                    <th>Account</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="tp-empty-cell">
                        No closed stock exits recorded yet.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => {
                      const display = accountDisplayFor(
                        row.brokerAccountId,
                        accounts,
                      );
                      const accountLabel =
                        display.badgeText === "UNKNOWN"
                          ? row.accountLabel
                          : display.label;
                      return (
                        <tr key={row.fillId}>
                          <td className="num">{fmtDateTime(row.exitTime)}</td>
                          <td className="ticker">
                            <SymbolLabel
                              symbol={row.symbol}
                              companyName={row.companyName}
                            />
                          </td>
                          <td>{row.side}</td>
                          <td className="num right">{fmtQty(row.qty)}</td>
                          <td className="num right">
                            {fmtPx(row.entryPrice)}
                            <div className="tp-row-sub">
                              {fmtDateTime(row.entryTime)}
                            </div>
                          </td>
                          <td className="num right">{fmtPx(row.exitPrice)}</td>
                          <td
                            className={
                              "num right " +
                              (row.realizedPnl >= 0 ? "pos" : "neg")
                            }
                          >
                            {fmtSignedMoney(row.realizedPnl)}
                          </td>
                          <td className="num right">{fmtR(row.rMultiple)}</td>
                          <td>{exitLabel(row.exitLegType)}</td>
                          <td className="num">
                            {row.strategyId ?? "N/A"}
                          </td>
                          <td>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 4,
                                alignItems: "flex-start",
                              }}
                            >
                              <AccountBadge display={display} />
                              <span className="tp-row-sub">
                                {accountLabel}
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
